import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  Output,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  LucideAngularModule,
  ArrowRight,
  ArrowLeft,
  Send,
} from 'lucide-angular';
import {
  SubmissionQuestion,
  QuestionType,
  SaveAnswerDto,
  STUDENT_ASSIGNMENT_THEME_COLOR,
} from '../../services/student-assignment.models';

@Component({
  selector: 'app-question-view',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule],
  templateUrl: './question-view.html',
  styleUrl: './question-view.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuestionViewComponent implements OnChanges {
  @Input() question: SubmissionQuestion | null = null;
  @Input() currentIndex = 0;
  @Input() totalQuestions = 0;
  @Input() answer: SaveAnswerDto | null = null;
  @Input() isSubmitting = false;

  @Output() readonly answerChange = new EventEmitter<SaveAnswerDto>();
  @Output() readonly next = new EventEmitter<void>();
  @Output() readonly previous = new EventEmitter<void>();
  @Output() readonly submit = new EventEmitter<void>();

  protected readonly themeColor = STUDENT_ASSIGNMENT_THEME_COLOR;
  protected readonly QuestionType = QuestionType;

  protected readonly icons = {
    ArrowRight,
    ArrowLeft,
    Send,
  };

  @ViewChild('textareaEl')
  private textareaEl?: ElementRef<HTMLTextAreaElement>;

  // Local state for text input
  protected textAnswer = '';
  protected selectedOptionId: string | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    // Sync local state when answer input changes
    if (changes['answer'] || changes['question']) {
      this.textAnswer = this.answer?.textAnswer || '';
      this.selectedOptionId = this.answer?.selectedOptionId || null;

      // Schedule auto-resize after the view updates with restored content
      setTimeout(() => this.autoResizeTextarea());
    }
  }

  protected get isFirstQuestion(): boolean {
    return this.currentIndex === 0;
  }

  protected get isLastQuestion(): boolean {
    return this.currentIndex >= this.totalQuestions - 1;
  }

  protected get hasAnswer(): boolean {
    if (!this.question) return false;

    if (this.question.type === QuestionType.MULTIPLE_CHOICE) {
      return !!this.selectedOptionId;
    }
    return this.textAnswer.trim().length > 0;
  }

  protected get questionTypeLabel(): string {
    if (!this.question) return '';

    switch (this.question.type) {
      case QuestionType.MULTIPLE_CHOICE:
        return 'Multiple Choice';
      case QuestionType.SHORT_ANSWER:
        return 'Short Answer';
      case QuestionType.ESSAY:
        return 'Essay';
      default:
        return '';
    }
  }

  protected onOptionSelect(optionId: string): void {
    this.selectedOptionId = optionId;
    this.answerChange.emit({ selectedOptionId: optionId });
  }

  protected onTextChange(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    this.textAnswer = textarea.value;
    this.autoResizeTextarea(textarea);
    this.answerChange.emit({ textAnswer: this.textAnswer });
  }

  private autoResizeTextarea(textarea?: HTMLTextAreaElement): void {
    const el = textarea || this.textareaEl?.nativeElement;
    if (!el) return;

    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }

  protected onNext(): void {
    this.next.emit();
  }

  protected onPrevious(): void {
    this.previous.emit();
  }

  protected onSubmit(): void {
    this.submit.emit();
  }
}
